/**
 * Shared types and constants for the ASK AI CSV download feature.
 *
 * This module is the scaffold every other module in `csvDownload/` imports
 * from: the grid shape the conversion core produces and consumes
 * (`tableToCellTextGrid.ts`, `serializeCsv.ts`), the per-table portal target
 * the detection effect builds (`AnswerTableDownloads.tsx`), the file-name
 * inputs (`buildCsvFileName.ts`), and the download state exposed to the
 * controls (`useTableCsvDownload.ts`, `DownloadControl.tsx`).
 *
 * It holds types and constants only — no logic, no React, no DOM access — so
 * it can be imported from pure modules and from property tests alike.
 */

// ── Data models ────────────────────────────────────────────────────────────

/**
 * Rectangular grid of trimmed cell texts. `grid[0]` is the header record.
 *
 * Two invariants hold for every value of this type:
 *   - It is rectangular: every row has the same length (Req 3.9).
 *     `tableToCellTextGrid` establishes this before returning, so
 *     `serializeCsv` needs no padding logic.
 *   - It holds cell text as displayed and carries no neutralization
 *     apostrophe. Neutralization happens in the serializer, which is what
 *     lets the round-trip property (Req 3.12) state an equality against the
 *     grid.
 */
export type CellTextGrid = string[][];

/** One detected table and the element the control is portalled into. */
export interface AnswerTableTarget {
  /** Zero-based position index within the answer (Req 1.3). */
  tableIndex: number;
  table: HTMLTableElement;
  /** Empty div created by the detection effect and removed on cleanup. */
  mountNode: HTMLElement;
}

/** Inputs to the file name (Req 4.1-4.4). */
export interface CsvFileNameParts {
  conversationId?: string | null;
  messageIndex: number;
  tableIndex: number;
  /** Injected so the name is deterministic in tests. */
  now: Date;
}

/** Phase of one download activation. */
export type DownloadPhase = "idle" | "confirming" | "converting";

/** Download state exposed to the controls of one answer. */
export interface DownloadState {
  phase: DownloadPhase;
  busyTableIndex: number | null;
  confirmRowCount: number | null;
  error: string | null;
}

// ── Thresholds and limits ──────────────────────────────────────────────────

/**
 * Data-row count above which a download activation asks for confirmation
 * before conversion starts (Req 6.1). Strictly above: a table with exactly
 * this many data rows converts without a prompt.
 */
export const LARGE_TABLE_ROW_THRESHOLD = 5000;

/**
 * Number of table rows the chunked grid build processes before yielding to
 * the event loop, so a very large table does not freeze the tab and the
 * progress indicator can paint (Req 6.4).
 */
export const CHUNK_ROWS = 500;

/**
 * Upper bound applied to a cell's `colspan` and `rowspan` when expanding it
 * into the grid (Req 3.7, 3.8). A hostile `colspan="1000000"` cannot make the
 * converter allocate an unbounded grid.
 */
export const MAX_SPAN = 1000;

// ── Serialization ──────────────────────────────────────────────────────────

/**
 * Byte order mark placed ahead of the CSV content so a spreadsheet
 * application reads non-ASCII cell text correctly (Req 3.11).
 */
export const CSV_BOM = "\uFEFF";
